import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../../components/AppIcon';
import Button from '../../../../components/ui/Button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogBody, DialogFooter } from '../../../../components/ui/Dialog';
import { rescheduleMatch } from '@/api/TournamentManagement/matchApi';
import type { MatchView } from '@/models/tournament';
import { useTournament } from '@/hooks/useTournament';
import { parseApiWallClock } from '@/utils/apiDate';

interface RescheduleMatchModalProps {
  isOpen: boolean;
  onClose: () => void;
  match: MatchView | null;
  onSuccess: () => void;
  showToast: (message: string, type: 'success' | 'error') => void;
}

const RescheduleMatchModal: React.FC<RescheduleMatchModalProps> = ({
  isOpen,
  onClose,
  match,
  onSuccess,
  showToast,
}) => {
  const { t, i18n } = useTranslation('tournament');
  const { data: tournament } = useTournament(match?.tournamentId ?? 0);
  const [newDateTime, setNewDateTime] = useState('');
  const [reason, setReason] = useState('');
  const [errors, setErrors] = useState<{ newDateTime?: string }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const formatTournamentDate = (dateStr: string): string => {
    return new Date(dateStr).toLocaleDateString(i18n.language, { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const validateForm = (): boolean => {
    const newErrors: { newDateTime?: string } = {};

    if (!newDateTime) {
      newErrors.newDateTime = t('Date is required');
    } else {
      const selectedDate = new Date(newDateTime);
      if (selectedDate <= new Date()) {
        newErrors.newDateTime = t('New date must be in the future');
      }
      const selectedDateStr = newDateTime.split('T')[0];
      if (!newErrors.newDateTime && tournament?.startDate && selectedDateStr < tournament.startDate.split('T')[0]) {
        newErrors.newDateTime = t('matchDateBeforeTournamentStart', { date: formatTournamentDate(tournament.startDate) });
      }
      if (!newErrors.newDateTime && tournament?.endDate && selectedDateStr > tournament.endDate.split('T')[0]) {
        newErrors.newDateTime = t('matchDateAfterTournamentEnd', { date: formatTournamentDate(tournament.endDate) });
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!validateForm() || !match) return;

    setIsSubmitting(true);
    try {
      const result = await rescheduleMatch({
        // Send wall-clock local (same format as match create/edit) — NOT toISOString().
        // Match.ScheduledDateTime is a wall-clock contract (the work-point night/early-morning
        // bonus reads its .Hour); the backend converts to UTC via the company timezone when it
        // writes the linked ScheduledShift. toISOString() would UTC-shift and double-convert.
        matchId: match.id,
        newScheduledDateTime: `${newDateTime}:00`,
        reason: reason || undefined,
      });

      if (result.errors.length > 0) {
        showToast(result.errors[0]?.description || t('Failed to reschedule match'), 'error');
        return;
      }

      showToast(t('Match rescheduled successfully'), 'success');
      handleClose();
      onSuccess();
    } catch {
      showToast(t('Failed to reschedule match. Please try again.'), 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setNewDateTime('');
    setReason('');
    setErrors({});
    onClose();
  };

  const formatDateTime = (dateTimeString: string): { date: string; time: string } => {
    const date = parseApiWallClock(dateTimeString) ?? new Date(dateTimeString);
    return {
      date: date.toLocaleDateString(i18n.language, { month: 'short', day: 'numeric', year: 'numeric' }),
      time: date.toLocaleTimeString(i18n.language, { hour: '2-digit', minute: '2-digit' }),
    };
  };

  return (
    <Dialog open={isOpen && !!match} onOpenChange={(open) => { if (!open) handleClose(); }}>
      <DialogContent size="default" data-testid="reschedule-match-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center">
            <Icon name="CalendarClock" size={24} className="me-2 text-violet-600" />
            {t('Reschedule Match')}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <DialogBody className="space-y-6">
            {/* Current Match Info */}
            <div className="bg-muted rounded-lg p-4">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">
                {t('Match Details')}
              </p>
              {match && (
                <>
                  <div className="flex items-center space-x-2 rtl:space-x-reverse mb-2">
                    <span className="text-sm font-semibold text-foreground">{match.team1Name}</span>
                    <span className="text-xs text-muted-foreground">{t('vs')}</span>
                    <span className="text-sm font-semibold text-foreground">{match.team2Name}</span>
                  </div>
                  <div className="flex items-center text-sm text-muted-foreground mb-1">
                    <Icon name="Calendar" size={14} className="me-2 text-muted-foreground" />
                    <span>
                      {formatDateTime(match.scheduledDateTime).date} &bull; {formatDateTime(match.scheduledDateTime).time}
                    </span>
                  </div>
                  <div className="flex items-center text-sm text-muted-foreground">
                    <Icon name="MapPin" size={14} className="me-2 text-muted-foreground" />
                    <span>{match.location}</span>
                  </div>
                </>
              )}
            </div>

            {/* New Date & Time */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-foreground">
                {t('New Date & Time')} <span className="text-red-500">*</span>
              </label>
              <input
                type="datetime-local"
                value={newDateTime}
                onChange={(e) => {
                  setNewDateTime(e.target.value);
                  if (errors.newDateTime) setErrors({});
                }}
                className={`w-full px-3 py-2 border rounded-md bg-background text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary transition-colors ${
                  errors.newDateTime ? 'border-red-300' : 'border-input'
                }`}
                disabled={isSubmitting}
                data-testid="reschedule-match-datetime"
              />
              {errors.newDateTime && (
                <p className="text-sm text-red-600 flex items-center mt-1">
                  <Icon name="AlertCircle" size={14} className="me-1" />
                  {errors.newDateTime}
                </p>
              )}
              {tournament?.startDate && tournament?.endDate ? (
                <p className="text-xs text-muted-foreground mt-1">
                  {t('tournamentDateRange', { start: formatTournamentDate(tournament.startDate), end: formatTournamentDate(tournament.endDate) })}
                </p>
              ) : (
                <p className="text-xs text-muted-foreground mt-1">{t('Must be within the tournament date range')}</p>
              )}
            </div>

            {/* Reason */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-foreground">
                {t('Reason for Rescheduling')}
                <span className="text-muted-foreground ms-1 font-normal">({t('Optional')})</span>
              </label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder={t('Provide a reason for rescheduling this match...')}
                rows={3}
                className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary transition-colors resize-none"
                disabled={isSubmitting}
                data-testid="reschedule-match-reason"
              />
            </div>
          </DialogBody>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isSubmitting}
              data-testid="reschedule-match-cancel-btn"
            >
              {t('Cancel')}
            </Button>
            <Button
              type="submit"
              variant="default"
              loading={isSubmitting}
              disabled={isSubmitting}
              iconName="CalendarClock"
              data-testid="reschedule-match-submit-btn"
            >
              {t('Reschedule Match')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default RescheduleMatchModal;
